<script setup lang="ts">
import { Chart } from 'highcharts-vue';
import DatapointDialog from './DatapointDialog.vue';
import { EnumerationDataPointDto, EnumerationDataSetDto, EvaluationState } from '@/api/generator';
import * as Highcharts from 'highcharts';
import { highchartColors, highchartSymbols, useMergeWithChartDefaults } from './utils';
import { getDateTimeFormatted } from '@/components/formatters/utils';
import { useTranslation } from 'i18next-vue';
import { computed, toRef } from 'vue';
import { getSetupParameterName } from '@/views/tasks/utils';

interface EnumerationTrendChartProps {
  datasets: EnumerationDataSetDto[];
  showInJobContext?: boolean;
  startDate?: string;
  endDate?: string;
  markedEvaluationResultIds?: string[];
}

const { t } = useTranslation();

const props = defineProps<EnumerationTrendChartProps>();

const yAxisId = 'enum';

const allCategories = computed(() => {
  let index = 0;
  return props.datasets.map((dataset) => {
    const targetIndex = index++;
    const targetColor =
      highchartColors[highchartColors.length - 1 - (targetIndex % highchartColors.length)];
    const targetSymbol =
      highchartSymbols[highchartSymbols.length - 1 - (targetIndex % highchartSymbols.length)]
        .symbolChar;

    const label = `${getSetupParameterName(dataset.evaluationParameter)} <strong style="color: ${targetColor}; font-size: 18px;">${targetSymbol}</strong>`;
    return visibleSeries.value.includes(dataset.id) ? label : '';
  });
});

function generateTooltip({
  dataset,
  data,
}: {
  dataset: EnumerationDataSetDto;
  data: EnumerationDataPointDto;
}) {
  const { commentCount, lastComment } = data;

  const creationTime = getDateTimeFormatted({
    value: data.creationTime,
  });

  const creationString = `+${creationTime} | ${data.creatorUserFullName}`;
  const valueLabel = t('Value');
  const latestCommentLabel = t('LatestComment');
  const resultLabel = t('Result_one');

  const evaluationText = t(`EvaluationStateEnum.${data.evaluationOutcome}`);

  let commentsString = '';
  if (commentCount) {
    const additional =
      commentCount > 1
        ? ` (+ ${commentCount - 1} ${t('Comments', { count: commentCount - 1 })})`
        : '';

    commentsString = `<br /><br />${latestCommentLabel}:<br />${lastComment}${additional}`;
  }

  return `
    <strong>${getSetupParameterName(dataset.evaluationParameter)}</strong><br />
    ${creationString}<br /><br />
    ${valueLabel}: <strong>${data.selectedOption.name}</strong><br />
    ${resultLabel}: <strong>${evaluationText}</strong>
    ${commentsString}
  `.trim();
}

const options = computed<Highcharts.Options>(() => {
  return {
    exporting: {
      enabled: false,
    },
    chart: {
      // calculate height based on numer of datasets (= rows)
      height: 90 + props.datasets.length * 60,
    },
    tooltip: {
      formatter: function () {
        return this.custom?.tooltip || '';
      },
    },
    yAxis: [
      {
        id: yAxisId,
        title: {
          text: '',
        },
        type: 'category',
        gridLineWidth: 0,
        categories: allCategories.value,
        // set mix and max to keep y-axis stable without zooming when series are set invisible
        min: 0,
        max: props.datasets.length - 1,
      },
    ],
    series: props.datasets.map<Highcharts.SeriesOptionsType>((dataset, index) => {
      const colorMap: Record<EvaluationState, string> = {
        Failed: 'rgb(var(--v-theme-error))',
        Passed: 'rgb(var(--v-theme-success))',
        Warning: 'rgb(var(--v-theme-warning))',
        WithoutEvaluation: 'rgb(var(--v-theme-primary-lighten-1))',
      };

      const iconMap: Record<EvaluationState, string> = {
        Failed: 'url(/error.svg)',
        Passed: 'url(/success.svg)',
        Warning: 'url(/warning.svg)',
        WithoutEvaluation: 'url(/withoutEvaluation.svg)',
      };

      return {
        id: dataset.id,
        yAxis: yAxisId,
        type: 'scatter',
        name: allCategories.value[index],
        keys: ['evaluationOutcome'],
        data: dataset.data.map<Highcharts.PointOptionsObject>((data) => {
          const color = colorMap[data.evaluationOutcome];

          return {
            id: data.id,
            x: data.creationTime,
            y: index,
            evaluationOutcome: `${data.selectedOption.name} - (${data.evaluationOutcome})`,
            // Keep this `custom`-prop as lightweight as possible
            custom: {
              tooltip: generateTooltip({ data, dataset }),
              commentCount: data.commentCount,
            },
            events: {
              click: () => onDatapointClick(data),
            },
            marker: {
              symbol: iconMap[data.evaluationOutcome],
              radius: 0,
              fillOpacity: 0,
              states: { hover: { radius: 0, enabled: false } },
              ...(props.markedEvaluationResultIds?.includes(data.id)
                ? {
                    height: 32,
                    width: 32,
                  }
                : {}),
            },
            color,
          };
        }),
      };
    }),
    plotOptions: {
      series: {
        dataLabels: {
          x: -12,
          y: 0,
        },
      },
    },
  };
});

const {
  chartComponent,
  onDatapointClick,
  zooming,
  zoomOut,
  selectedDatapointId,
  targetOptions,
  visibleSeries,
} = useMergeWithChartDefaults({
  options,
  showInJobContext: toRef(() => props.showInJobContext),
  startDate: toRef(() => props.startDate),
  endDate: toRef(() => props.endDate),
});
</script>

<template>
  <v-sheet v-if="datasets?.length" class="pa-6 position-relative">
    <Chart ref="chartComponent" :options="targetOptions"></Chart>
    <v-btn v-if="zooming" class="mt-4" @click="zoomOut">Reset Zoom</v-btn>
    <DatapointDialog v-if="!showInJobContext" v-model:datapointId="selectedDatapointId" />
  </v-sheet>
</template>

<style lang="scss" scoped>
.downloadButton {
  position: absolute;
  right: 10px;
  top: 10px;
}
</style>
